import React, { useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core';
import { IdPropType } from '../../../proptypes/basic';
import MailDraftView from './MailDraftView';
import DraftsList from './DraftsList';
import { useUserEmployee, userHasUserEmployeeEmailAccess } from '../../auth/hooks';
import { useEmailDraft } from '../emailDraftsSlice';
import { useSnackbar } from '../../snackbar/hooks';

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
    },

    list: {
        maxWidth: 450,
        height: '100%',
        overflowY: 'auto',
        borderRight: `1px solid ${theme.palette.grey[300]}`,
    },

    content: {
        overflowX: 'auto',
        height: '100%',
    },
}));

const createListId = (customerId = null) => {
    if (customerId) {
        return `emailDrafts.${customerId}.list`;
    }
    return 'emailDrafts.list';
};

const MailDrafts = ({ customerId, draftId, selectedFolder, onChangeFolder }) => {
    const classes = useStyles();
    // If draftId is provided, use it as initial state, otherwise null
    const { employee: userEmployee } = useUserEmployee();
    const [draft] = useEmailDraft(draftId);

    const hasAccess = userHasUserEmployeeEmailAccess(userEmployee, draft?.emailInboxId);
    const [selected, setSelected] = useState(draftId ?? null);
    // Use a numeric toggle to guarantee an effect execution inside DraftsList on every increment
    const [reloadToggle, setReloadToggle] = useState(0);
    const { enqueueSnackbar , closeSnackbar} = useSnackbar();

    // If draftId changes (e.g. via route), update selected only if it was null before
    useEffect(() => {
        if (draftId && selected === null && draft) {
            if (!hasAccess) {
                //console.log('MailInbox: User does not have access to this email inbox:', mail?.emailInboxId);
                setSelected(null);
                setReloadToggle(prev => prev + 1);

                // Show a snackbar with the error message
                let errorMessage = `Kein Zugriff auf angeforderte E-Mail! Postfach: ${draft?.emailInboxId}! => Zeige Default-Postfach`;
                enqueueSnackbar(
                    errorMessage,
                    {
                        key: 'no-access-inbox',
                        variant: 'error',
                        persist: true,
                        style: { whiteSpace: 'pre-line' },
                        preventDuplicate: true
                    }
                );
            }else {
                closeSnackbar('no-access-inbox');
                setSelected(draftId);
            }
        }
    }, [draftId, selected, draft, hasAccess, closeSnackbar, enqueueSnackbar]);

    // handle the on delete callback from MailDraftView to reset selected draft
    const handleDraftDeleted = () => {
        draftId = null;
        setSelected(null);
        setReloadToggle(prev => prev + 1);
        enqueueSnackbar('Entwurf gelöscht', { variant: 'success' });
    };

    const handleDraftSent = () => {
        draftId = null;
        setSelected(null);
        setReloadToggle(prev => prev + 1);
        enqueueSnackbar('Entwurf gesendet', { variant: 'success' });
    };
    const handleCloseWithoutSent = () => {
        draftId = null;
        setSelected(null);
        // reload the drafts list
        setReloadToggle(prev => prev + 1);

        enqueueSnackbar('Entwurf nicht gesendet. Änderungen wurden gespeichert', { variant: 'success' });
    };

    return (
        <Grid container className={classes.root}>
            <Grid item xs={4} className={classes.list}>
                <DraftsList
                    listId={createListId(customerId)}
                    customerId={customerId}
                    selected={selected}
                    onSelect={setSelected}
                    reloadToggle={reloadToggle}
                    selectedFolder={selectedFolder}
                    onChangeFolder={onChangeFolder}
                />
            </Grid>
            <Grid item xs className={classes.content}>
                <MailDraftView 
                    customerId={customerId}
                    draftId={selected} 
                    popup={true}
                    onDeleted={handleDraftDeleted}
                    onSent={handleDraftSent}
                    onCloseWithoutSent={handleCloseWithoutSent}
                     />
            </Grid>
        </Grid>
    );
};

MailDrafts.propTypes = {
    customerId: IdPropType,
    draftId: IdPropType,
    selectedFolder: PropTypes.string.isRequired,
    onChangeFolder: PropTypes.func.isRequired,
};

MailDrafts.defaultProps = {
    customerId: null,
    draftId: null,
};

export default MailDrafts;
